import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "clutch/src/Button/Button.jsx";
import { TextField } from "clutch/src/TextField/TextField.jsx";

import {
  Form,
  FormInput,
} from "@/feature-auth/components/EditEmailModal.style.jsx";
import { isEmailTaken } from "@/feature-auth/fetches/get-is-email-taken.mjs";
import {
  changeEmail,
  updateUserEmailInState,
} from "@/feature-auth/utils/auth-actions.mjs";
import CheckMark from "@/inline-assets/settings-check-mark.svg";
import MessageIcon from "@/inline-assets/settings-message.svg";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogWindow,
} from "@/shared/Dialog.jsx";
import { devWarn } from "@/util/dev.mjs";

const EditEmailModal = ({ user, close, setSnackbarText }) => {
  const email = user?.email ?? "";

  const { t } = useTranslation();
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [verifyingEmails, setVerifyingEmails] = useState(false);
  const [emails, setEmails] = useState([]);

  const showError = useMemo(
    () => (emailError?.length ? emailError : ""),
    [emailError],
  );

  const newEmailIsVerified = useMemo(
    () => emails.includes(newEmail),
    [emails, newEmail],
  );
  const oldEmailIsVerified = useMemo(
    () => emails.includes(email),
    [emails, email],
  );

  const onChangeEmail = useCallback(
    (event) => {
      setNewEmail(event.target.value);
      if (event.target.value === email) {
        setEmailError(
          t(
            "common:account.changeEmail.sameEmailError",
            "Can't use the same email.",
          ),
        );
      } else {
        setEmailError("");
      }
    },
    [setNewEmail, email, t],
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        const isTaken = await isEmailTaken(newEmail);
        if (isTaken) {
          setEmailError(
            t(
              "common:account.changeEmail.emailTakenError",
              "* This email is already taken.",
            ),
          );
          return;
        }

        setVerifyingEmails(true);
        await changeEmail(newEmail, setEmails);
      } catch (err) {
        devWarn("Error with email taken check", err);
      }
    },
    [newEmail, t],
  );

  useLayoutEffect(() => {
    if (!verifyingEmails) return;

    let timeOut;
    if (emails.includes(email) && emails.includes(newEmail)) {
      updateUserEmailInState(newEmail);
      timeOut = setTimeout(() => {
        close(null);
      }, 1000);
    }

    //callback displaying a sucessfuly save using a snackbar
    setSnackbarText([
      "common:account.changeEmail.emailAddressUpdated",
      "Email address updated",
    ]);

    return () => clearTimeout(timeOut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emails]);

  return (
    <DialogWindow minWidth={400} maxWidth={500}>
      <DialogContent>
        <DialogHeader
          Icon={<MessageIcon />}
          title={
            verifyingEmails && !emailError
              ? t(
                  "common:account.changeEmail.verifyEmailAddresses",
                  "Verify Email Addresses",
                )
              : t(
                  "common:account.changeEmail.enterNewEmailAddress",
                  "Enter New Email Address",
                )
          }
        />

        <Form onSubmit={onSubmit}>
          <DialogBody className="edit-info">
            {verifyingEmails && !emailError ? (
              <>
                <div className="type-body2">
                  {t(
                    "common:account.changeEmail.emailsSent",
                    "Emails have been sent to the email addresses below:",
                  )}
                </div>

                <FormInput>
                  <label className="type-caption" htmlFor="email">
                    {t("common:settings.newEmail", "New Email")}
                  </label>

                  <TextField
                    id="email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    value={newEmail}
                    disabled
                  >
                    {newEmailIsVerified && (
                      <div className="check-mark">
                        <CheckMark />
                      </div>
                    )}
                  </TextField>
                </FormInput>

                <FormInput>
                  <label className="type-caption" htmlFor="email">
                    {t("common:account.changeEmail.oldEmail", "Old Email")}
                  </label>

                  <TextField
                    id="email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    value={email}
                    disabled
                  >
                    {oldEmailIsVerified && (
                      <div className="check-mark">
                        <CheckMark />
                      </div>
                    )}
                  </TextField>
                </FormInput>
              </>
            ) : (
              <FormInput>
                <label className="type-caption" htmlFor="email">
                  {t("common:email", "Email")}
                </label>

                <TextField
                  type="email"
                  name="email"
                  required
                  placeholder={t(
                    "common:account.yourEmailPlaceholder",
                    "Your Email Address...",
                  )}
                  onChange={onChangeEmail}
                  value={newEmail}
                  error={showError}
                  errorInline
                />
              </FormInput>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="cancel"
              emphasis="low"
              size="large"
              onClick={() => close(null)}
            >
              {t("common:cancel", "Cancel")}
            </Button>

            {!verifyingEmails && (
              <Button
                type="submit"
                emphasis="high"
                size="large"
                disabled={emailError.length > 0}
              >
                {t("common:settings.confirm", "Confirm")}
              </Button>
            )}
          </DialogFooter>
        </Form>
      </DialogContent>
    </DialogWindow>
  );
};

export default EditEmailModal;
